import {browserHistory as history} from 'react-router';
import store from './store';
import moment from 'moment';

import {
    SESSION_LOAD,
    SESSION_LOGIN,
    SESSION_LOGOUT,
    SESSIONITEMS_LOAD,
    SESSIONITEMS_UNLOAD,
    SESSIONITEMS_SET
} from './actionDefinitions';
import {
    getServerState,
    deleteSession,
    postSession,
    watchSessionItems,
    unwatchSessionItems,
    postForgotPassword,
    getTemporaryLink,
    postResetPassword,
    postUser,
    getClient
} from './sessionApi';
import {
    watchSso,
    unwatchSso
} from './api/SamlApi';
import {updateHeaders} from './apiUtils';
import {SurveyType} from './generated';

let sessionTimer;
const sessionTimerInterval = 60000;

export function initialize() {
    sessionTimer = setInterval(checkSession, sessionTimerInterval);
    return dispatch => {
        let isSurveyMode = window.location.search.includes('surveyMode');
        if (isSurveyMode) dispatch(setSurveyMode());
        dispatch(loadServerState());
        const browserStorage = {...window.localStorage, ...window.sessionStorage};
        let session = browserStorage.session ? JSON.parse(browserStorage.session) : {};
        if (session.name && session.token && session.expires) {
            dispatch({type: SESSION_LOAD, payload: session});
            dispatch(pollSessionItems());

            if (isSurveyMode)
                history.push('/agentsurvey');
            else if (window.location.pathname.toLowerCase().startsWith('login', 1))
                history.push('/systemdashboard');
        } else if (
            !window.location.pathname.toLowerCase().startsWith('login', 1) &&
            !window.location.pathname.toLowerCase().startsWith('resetpassword', 1) &&
            !window.location.pathname.toLowerCase().startsWith('customersurvey', 1) &&
            !window.location.pathname.toLowerCase().startsWith('newhire', 1) &&
            !window.location.pathname.toLowerCase().startsWith('survey', 1) &&
            !window.location.pathname.toLowerCase().startsWith('ssologin', 1)
        ) {
            history.push('/login');
        }
    };
}

function loadServerState() {
    return dispatch =>
        getServerState()
            .then(payload => dispatch({type: SESSION_LOAD, payload: {serverState: payload.Result}}))
            .catch(payload =>
                dispatch({
                    type: SESSION_LOAD,
                    error: true,
                    payload: {
                        statusCode: payload.status,
                        message: payload.bodyContent
                            ? payload.bodyContent.error_description
                            : false || payload.statusText,
                        serverState: {EnvironmentType: -1, InstanceStartTime: undefined}
                    }
                })
            );
}

export function pollSso(email) {
    unwatchSso();
    return dispatch =>
        watchSso(email)
            .on('success', payload => {
                const ssoResults = payload.Result;
                if (ssoResults) {
                    unwatchSso();
                    dispatch({
                        type: SESSION_LOGIN,
                        payload: {userNameFromReset: undefined, passwordFromReset: undefined}
                    });
                    dispatch(login(email, '6A0DDED9385843C58BE6C738B8BC1EB3', false, '/portal', false));
                }
            })
            .on('error', payload => dispatch({type: SESSION_LOGIN, error: true, payload}))
            .start();
}

function checkSession() {
    let session = store.getState().session;
    if (session.expires && session.expires <= Date.now() + sessionTimerInterval)
        store.dispatch(logout(session, 'Your session expired'));
    if (!session.serverState || session.serverState.EnvironmentType < 1) store.dispatch(loadServerState());
}

export function login(userName, password, rememberMe, targetPath, desktopAttemptingLogin) {
    const sessionState = store.getState().session;
    const {surveyMode} = sessionState;
    return dispatch => {
        dispatch({type: SESSION_LOGIN, isFetching: true});
        return postSession(userName, password, desktopAttemptingLogin)
            .then(payload => {
                updateHeaders({Authorization: 'Bearer ' + payload.access_token});
                payload.token = payload.access_token;
                payload.expires = Date.parse(payload['.expires']);
                dispatch({type: SESSION_LOGIN, payload, isFetching: false});

                try {
                    (rememberMe ? window.localStorage : window.sessionStorage).session = JSON.stringify(payload);
                } catch (e) {
                    alert('Unable to preserve session, probably due to being in private browsing mode.');
                }

                dispatch(pollSessionItems());

                if (surveyMode) history.push('agentsurvey');
                else history.push(targetPath);
            })
            .catch(payload =>
                dispatch({
                    type: SESSION_LOGIN,
                    error: true,
                    payload: {
                        statusCode: payload.status,
                        message: payload.bodyContent.error_description || payload.statusText
                    }
                })
            );
    }
}

export function logout(session, message) {
    return dispatch => {
        const sessionState = store.getState().session;
        dispatch({type: SESSION_LOGOUT});
        deleteSession(session);
        updateHeaders({Authorization: undefined});
        try {
            if (sessionStorage.session) sessionStorage.removeItem('session');
            if (localStorage.session) localStorage.removeItem('session');
        } catch (e) {
            // ignore
        }
        dispatch(cancelPollSessionItems());
        window.location.href =
            '/login' + (message ? `/logoutmessage/${message}` : '') + (sessionState.surveyMode ? '?surveyMode' : ''); // reload fully
    };
}

export function pollSessionItems() {
    unwatchSessionItems();
    return dispatch => {
        watchSessionItems()
            .on('success', payload => {
                const {
                    Client: currentClient,
                    User: currentUser,
                    Signals: signals,
                    SurveyAssignments: surveyAssignments,
                    DatabaseTime: databaseTime,
                    fileMaxRequestLength
                } = payload.Result;
                const databaseTimeCorrectionMs = moment.utc() - moment.utc(databaseTime);
                let calibratorSurveyAssignments, internalSurveyAssignments;
                if (surveyAssignments) {
                    calibratorSurveyAssignments = surveyAssignments.filter(
                        o => o.RespondWithSurveyType === SurveyType.Calibrator
                    );
                    internalSurveyAssignments = surveyAssignments.filter(
                        o => o.RespondWithSurveyType === SurveyType.MasterCalibratorResponse
                    );
                }
                if (currentClient) currentClient.GetSetting = getSetting.bind(currentClient);
                if (currentUser && currentUser.ForceRefresh) dispatch(updateUserAndRefresh(currentUser));
                dispatch({
                    type: SESSIONITEMS_LOAD,
                    payload: {
                        currentClient,
                        currentUser,
                        signals,
                        userName: currentUser.Name || currentUser.UserName,
                        surveyAssignments,
                        databaseTimeCorrectionMs,
                        calibratorSurveyAssignments,
                        internalSurveyAssignments,
                        fileMaxRequestLength
                    }
                });
            })
            .on('error', payload => dispatch({type: SESSIONITEMS_LOAD, error: true, payload}))
            .start();
    };
}

export function cancelPollSessionItems() {
    unwatchSessionItems();
    return {type: SESSIONITEMS_UNLOAD};
}

export function updateUserAndRefresh(user) {
    // set flag to false, then refresh.
    user.ForceRefresh = false;
    return dispatch =>
        postUser(user)
            .then(payload => {
                // Verify Saved
                if (payload.Result[0].ForceRefresh === false) window.location.reload(true);
            })
            .catch(payload => dispatch({type: SESSIONITEMS_LOAD, error: true, payload}));
}

function getSetting(key) {
    if (this.Settings) {
        const setting = this.Settings.find(o => o.Key === key);
        if (setting) return setting.Value;
    }
    return undefined;
}

export function setForgotPassword(forgotPassword) {
    return {type: SESSION_LOAD, payload: {forgotPassword}};
}

export function sendPasswordResetEmail(userName) {
    return dispatch =>
        postForgotPassword(userName, 'ResetPassword')
            .then(payload =>
                dispatch({
                    type: SESSION_LOAD,
                    payload: {
                        processingRequest: undefined,
                        resetError: undefined,
                        forgotPasswordResult: payload
                    }
                })
            )
            .catch(payload =>
                dispatch({
                    type: SESSION_LOAD,
                    payload: {
                        processingRequest: undefined,
                        resetStatusCode: payload.status,
                        resetError:
                            (payload.bodyContent &&
                                (payload.bodyContent.Message || payload.bodyContent.error_description)) ||
                            payload.statusText ||
                            payload.message,
                        forgotPasswordResult:
                            (payload.bodyContent &&
                                (payload.bodyContent.Message || payload.bodyContent.error_description)) ||
                            payload.statusText ||
                            payload.message
                    }
                })
            );
}

export function loadTemporaryLink(temporaryLinkText) {
    return dispatch =>
        getTemporaryLink(temporaryLinkText)
            .then(payload =>
                dispatch({
                    type: SESSION_LOAD,
                    payload: {
                        processingRequest: undefined,
                        resetError: undefined,
                        temporaryLink: payload
                    }
                })
            )
            .catch(payload =>
                dispatch({
                    type: SESSION_LOAD,
                    payload: {
                        processingRequest: undefined,
                        temporaryLinkStatusCode: payload.status,
                        resetError:
                            (payload.bodyContent &&
                                (payload.bodyContent.Message || payload.bodyContent.error_description)) ||
                            payload.statusText ||
                            payload.message,
                        resetPasswordResult:
                            (payload.bodyContent &&
                                (payload.bodyContent.Message || payload.bodyContent.error_description)) ||
                            payload.statusText ||
                            payload.message
                    }
                })
            );
}

export function resetPassword(temporaryLinkText, password1, password2) {
    return dispatch =>
        postResetPassword(temporaryLinkText, password1, password2)
            .then(payload =>
                dispatch({
                    type: SESSION_LOAD,
                    payload: {
                        processingRequest: undefined,
                        resetError: undefined,
                        resetPasswordResult: 'Password Reset. Login.',
                        userNameFromReset: payload.Result.UserName,
                        passwordFromReset: password1
                    }
                })
            )
            .catch(payload =>
                dispatch({
                    type: SESSION_LOAD,
                    payload: {
                        processingRequest: undefined,
                        temporaryLinkStatusCode: payload.status,
                        resetError:
                            (payload.bodyContent &&
                                (payload.bodyContent.Message || payload.bodyContent.error_description)) ||
                            payload.statusText ||
                            payload.message,
                        resetPasswordResult:
                            (payload.bodyContent &&
                                (payload.bodyContent.Message || payload.bodyContent.error_description)) ||
                            payload.statusText ||
                            payload.message
                    }
                })
            );
}

export function setSurveyMode() {
    return {type: SESSION_LOAD, payload: {surveyMode: true}};
}

export function setClientIdAndUserId(clientId, userId) {
    return {type: SESSIONITEMS_SET, payload: {clientId: clientId, userId: userId}};
}

export function setCurrentClientByClientId(clientId) {
    return dispatch =>
        getClient(clientId).then(payload =>
            dispatch({
                type: SESSIONITEMS_SET,
                payload: {currentClient: payload}
            })
        );
}
